import type { ItemPriceHistory } from '@morales/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { PeriodBar, presets, usePeriod } from '../components/PeriodBar';
import { Empty, PageHead, QueryView } from '../components/ui';
import { formatDate, formatMoney, formatQty } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

const RANGES = presets('d365', 'd90', 'd30');

/**
 * The price history of one item (decision 123): what we paid (every vendor, the dock with freight, bills, lists,
 * placed orders), what we set (list prices per tier, customers' own prices), what we charged week by week with the
 * margin, and what each customer paid. For any range: profitability and pricing intelligence over time.
 */
export function PriceHistoryPage() {
  const { t, intlLocale } = useLocale();
  const { id = '' } = useParams();
  const period = usePeriod(RANGES);
  const query = useQuery({
    queryKey: qk.priceHistory(id, period.from, period.to),
    queryFn: ({ signal }) => api.priceHistory(id, { from: period.from, to: period.to }, signal),
    placeholderData: keepPreviousData,
  });
  const money = (v: number | null) => (v === null ? '—' : formatMoney(v, intlLocale));
  const day = (d: string) => formatDate(d, intlLocale, 'date');
  const pct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);
  const known = (v: number) => `${Math.round(v * 100)}%`;
  return (
    <QueryView query={query}>
      {(h: ItemPriceHistory) => (
        <div className="stack">
          <PageHead title={`${t('phTitle')}: ${h.itemName}`} back={`/items/${h.itemId}`} />
          <p className="muted">{t('phHint')}</p>
          <div className="toolbar">
            <PeriodBar presets={RANGES} period={period} />
          </div>

          <section className="card stack-sm wide">
            <h2>{t('phCharged')}</h2>
            {h.weeks.length === 0 ? (
              <Empty>{t('phNone')}</Empty>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('phWeek')}</th>
                      <th className="num">{t('phPacks')}</th>
                      <th className="num">{t('revenue')}</th>
                      <th className="num">{t('phAvgPrice')}</th>
                      <th className="num">{t('phAvgCost')}</th>
                      <th className="num">{t('grossMarginCol')}</th>
                      <th className="num">{t('gmCol')}</th>
                      <th className="num" title={t('costCoverageHint')}>{t('costKnownCol')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...h.weeks].reverse().map((w) => (
                      <tr key={w.week}>
                        <td>{day(w.week)}</td>
                        <td className="num">{formatQty(w.packs)} {h.packUnit ?? ''}</td>
                        <td className="num">{money(w.revenue)}</td>
                        <td className="num">{money(w.avgPrice)}</td>
                        <td className="num">{money(w.avgCost)}</td>
                        <td className={`num${w.margin !== null && w.margin < 0 ? ' bad' : ''}`}>{money(w.margin)}</td>
                        <td className="num">{pct(w.marginPct)}</td>
                        <td className={`num${w.costKnown < 0.98 ? ' warn' : ''}`}>{known(w.costKnown)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card stack-sm wide">
            <h2>{t('phCustomers')}</h2>
            {h.customers.length === 0 ? (
              <Empty>{t('phNone')}</Empty>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('customer')}</th>
                      <th className="num">{t('phPacks')}</th>
                      <th className="num">{t('revenue')}</th>
                      <th className="num">{t('phAvgPrice')}</th>
                      <th className="num">{t('phLastPrice')}</th>
                      <th className="num">{t('gmCol')}</th>
                      <th className="num" title={t('costCoverageHint')}>{t('costKnownCol')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {h.customers.map((c) => (
                      <tr key={c.customerId}>
                        <td>
                          <Link to={`/customers/${c.customerId}`}>{c.customerName}</Link>
                        </td>
                        <td className="num">
                          {formatQty(c.packs)} {h.packUnit ?? ''}
                        </td>
                        <td className="num">{money(c.revenue)}</td>
                        <td className="num">{money(c.avgPrice)}</td>
                        <td className="num">
                          {money(c.lastPrice)}
                          {c.lastUnit && c.lastUnit !== h.packUnit ? ` / ${c.lastUnit}` : ''} · {day(c.lastOn)}
                        </td>
                        <td className="num">{pct(c.marginPct)}</td>
                        <td className={`num${c.costKnown < 0.98 ? ' warn' : ''}`}>{known(c.costKnown)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card stack-sm wide">
            <h2>{t('phPaid')}</h2>
            {h.paid.length === 0 ? (
              <Empty>{t('phNone')}</Empty>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('phDate')}</th>
                      <th>{t('phVendor')}</th>
                      <th>{t('phSource')}</th>
                      <th className="num">{t('phVendorPrice')}</th>
                      <th className="num">{t('phFreight')}</th>
                      <th className="num">{t('cost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {h.paid.map((p, i) => (
                      <tr key={`${p.date}-${i}`}>
                        <td>{day(p.date)}</td>
                        <td>{p.vendorName}</td>
                        <td>
                          {t(`phSrc_${p.source}` as 'phSrc_list')}
                          {p.unit ? ` · ${p.unit}` : ''}
                        </td>
                        <td className="num">{money(p.vendorCost)}</td>
                        <td className="num">{p.freight === null ? '' : money(p.freight)}</td>
                        <td className="num">{money(p.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card stack-sm wide">
            <h2>{t('phSet')}</h2>
            {h.set.length === 0 ? (
              <Empty>{t('phNone')}</Empty>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('phDate')}</th>
                      <th>{t('phFor')}</th>
                      <th className="num">{t('phPrice')}</th>
                      <th className="num">{t('phCostBasis')}</th>
                      <th>{t('phBy')}</th>
                      <th>{t('phNote')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {h.set.map((s, i) => (
                      <tr key={`${s.date}-${i}`}>
                        <td>{day(s.date)}</td>
                        <td>{s.kind === 'list' ? `${t('phList')} · ${s.tierCode ?? ''}` : `${t('phRule')} · ${s.customerName ?? ''}`}</td>
                        <td className="num">{s.price !== null ? money(s.price) : s.marginPct !== null ? `${s.marginPct}% ${t('phMarginRule')}` : '—'}</td>
                        <td className="num">{s.costBasis === null ? '' : money(s.costBasis)}</td>
                        <td>
                          {s.by === 'auto' ? t('priceByAuto') : (s.by ?? '')}
                          {s.mode ? <span className="cell-sub">{t(s.mode === 'auto' ? 'priceAuto' : 'priceHand')}</span> : null}
                        </td>
                        <td>
                          {[s.note, s.until ? `${t('phUntil')} ${day(s.until)}` : null, s.endedOn ? `${t('phEnded')} ${day(s.endedOn)}` : null].filter(Boolean).join(' · ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </QueryView>
  );
}
